import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import BlogManager from "@/components/admin/BlogManager";
import IntegrationPanel from "@/components/admin/IntegrationPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function Admin() {
  const location = useLocation();
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("enquiries");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthState(false);
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (location.state?.user) {
      fetchLeads();
      return;
    }
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
        setAuthState(true);
        fetchLeads();
      } catch {
        setAuthState(false);
      }
    };
    check();
  }, [fetchLeads, location.state]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signInWithGoogle = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setAuthState(false);
    setUser(null);
    setLeads([]);
  };

  if (authState === null) {
    return (
      <section className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-secondary-foreground" />
      </section>
    );
  }

  if (!authState) {
    return (
      <section className="min-h-screen flex items-center justify-center px-6">
        <div
          data-testid="admin-login-card"
          className="w-full max-w-sm border border-border bg-card/60 p-10 text-center"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-4">Varun Marbles</p>
          <h1 className="font-serif text-3xl font-light tracking-tight mb-3">Admin</h1>
          <p className="text-sm font-light text-muted-foreground leading-relaxed mb-10">
            Sign in with the authorised Google account to manage enquiries, blog and integrations.
          </p>
          <Button
            onClick={signInWithGoogle}
            data-testid="admin-google-signin-btn"
            className="w-full rounded-none bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase py-6 transition-colors duration-300 hover:bg-foreground/80"
          >
            Sign in with Google
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-28 min-h-screen" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-4">
            Admin{user?.email ? ` — ${user.email}` : ""}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">Dashboard</h1>
        </div>
        <Button
          variant="outline"
          data-testid="admin-logout-btn"
          onClick={logout}
          className="rounded-none text-xs tracking-[0.2em] uppercase px-6 py-5"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-12 border-b border-border" data-testid="admin-tabs">
        {[
          { id: "enquiries", label: `Enquiries (${leads.length})` },
          { id: "blog", label: "Blog" },
          { id: "integrations", label: "Integrations" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`text-xs tracking-[0.2em] uppercase px-5 py-3.5 border-b-2 -mb-px transition-colors duration-300 ${
              tab === t.id
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "enquiries" && (
        <>
          <div className="flex justify-end mb-6">
            <Button
              variant="outline"
              data-testid="admin-refresh-btn"
              onClick={fetchLeads}
              disabled={loading}
              className="rounded-none text-xs tracking-[0.2em] uppercase px-6 py-5"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
          {leads.length === 0 && !loading ? (
            <p data-testid="admin-empty-state" className="text-muted-foreground font-light py-20 text-center border border-border">
              No enquiries yet. New submissions from the contact form will appear here.
            </p>
          ) : (
            <div className="border border-border overflow-x-auto" data-testid="admin-leads-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs tracking-[0.15em] uppercase">Date</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase">Name</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase">Phone</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase">Email</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase">Interested in</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id} data-testid={`admin-lead-row-${lead.id}`}>
                      <TableCell className="whitespace-nowrap text-sm font-light">
                        {new Date(lead.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell className="whitespace-nowrap">{lead.phone}</TableCell>
                      <TableCell>{lead.email || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">{lead.category}</TableCell>
                      <TableCell className="max-w-xs truncate">{lead.message || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      {tab === "blog" && <BlogManager />}
      {tab === "integrations" && <IntegrationPanel />}
    </section>
  );
}
